/**
 * MagazineViewPage.js — Vista de una edición como revista digital.
 *
 * Usa StPageFlip (page-flip) para el efecto de páginas voltando
 * con curl real, sombras dinámicas y gestos táctiles.
 *
 * Estructura de páginas:
 *   [0]        Portada
 *   [1]        Índice (parte 1)
 *   [2]        Índice (parte 2, si hay muchos artículos)
 *   [3...]     Artículos, uno por página (o dividido si es largo)
 *   [N]        Contraportada
 *
 * Renderizado: cada página es un <div> con clase .page, marcada
 * con data-density='hard' para portada/contra (páginas rígidas) o
 * 'soft' para artículos (voltean con curl).
 */

import { useEffect, useRef, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { renderToStaticMarkup } from 'react-dom/server';
import { PageFlip } from 'page-flip';
import { ChevronLeft, ChevronRight, X, BookOpen, List } from 'lucide-react';
import { editionService, maquetaService, publicidadService } from '../services/api';
import SopaInteractiva from '../components/revista/SopaInteractiva';
import ReproductorPieza from '../components/revista/ReproductorPieza';
// Logo de la hoja en blanco inicial del flipbook (la que deja la portada a la
// derecha del primer pliego). Antes era logo1.png, el de Fenagro.
import logoImg from '../assets/logo_comercio_colaborativo.png';
import styles from './MagazineViewPage.module.css';

// Cuánto texto cabe por página (aproximado; se ajusta por altura al render)
const CHARS_POR_PAGINA_ARTICULO = 1900;

export default function MagazineViewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const bookRef = useRef(null);           // <div> que PageFlip controla
  const stageRef = useRef(null);          // wrapper con overflow:hidden que clipea el staging
  const pageFlipRef = useRef(null);       // instancia de PageFlip
  const cleanupRef = useRef(null);        // { pf, onClickInBook, node } para cleanup del IIFE async
  const [edition, setEdition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [viewportSize, setViewportSize] = useState(() => {
    const f = (() => { try { return localStorage.getItem('news:formatoRevista') || 'a4'; } catch { return 'a4'; } })();
    return computeSize(f);
  });
  // Si la edición tiene maqueta impresa, aquí guardamos los data-URLs de
  // cada página del PDF renderizadas con pdf.js. Cuando esto tiene valor,
  // el flipbook se arma con estas imágenes en vez de con el flow dinámico
  // por categorías. Null = todavía no chequeado o no hay maqueta.
  const [paginasPdf, setPaginasPdf] = useState(null);
  const [cargandoPdf, setCargandoPdf] = useState(false);
  // Indice de contenido de la maqueta impresa: [{articuloId, titulo,
  // categoria, pagina}]. La guia de contenido de la version web se arma con
  // las paginas web, que en modo impreso no existen; por eso el modo impreso
  // necesita su propio indice, que viene del backend con la pagina fisica
  // real de cada articulo en la maqueta.
  const [indiceImpreso, setIndiceImpreso] = useState(null);
  // Bloques jugables de la maqueta: [{pagina, tipoContenido, datos}]. Se usan
  // para superponer la sopa de letras sobre la pagina del PDF donde esta
  // impresa. La cuadricula es la misma, porque el backend la genera con el id
  // de la edicion como semilla.
  const [interactivos, setInteractivos] = useState(null);
  const [jugando, setJugando] = useState(false);
  // Progreso de renderizado de la maqueta impresa. Al abrir la vista revista
  // y descargar un PDF de N páginas, mostramos "Preparando página X de N".
  const [progresoPdf, setProgresoPdf] = useState({ actual: 0, total: 0 });
  // Indicador durante la reinicialización del flipbook — cuando el usuario
  // cambia entre versión web e impresa, StPageFlip debe destruirse y volver
  // a montarse con el nuevo array de páginas. Ese proceso puede tomar 1-3s
  // si las páginas son imágenes grandes; sin overlay parece que se colgó.
  const [reiniciandoFlipbook, setReiniciandoFlipbook] = useState(false);
  // El lector puede alternar entre versión "web" (portada + índice + artículos
  // por categoría, siempre disponible) y versión "impresa" (PDF maquetado,
  // solo si la edición tiene maqueta). Preferencia persistida en localStorage.
  const [modoRevista, setModoRevista] = useState(() => {
    try { return localStorage.getItem('news:modoRevista') || 'web'; }
    catch { return 'web'; }
  });
  const cambiarModo = (nuevo) => {
    setModoRevista(nuevo);
    try { localStorage.setItem('news:modoRevista', nuevo); } catch {}
  };
  // Formato del canvas de la versión web:
  //   'a4'       → hoja A4 vertical, 1 artículo por página (default).
  //   'tabloide' → tabloide horizontal, 2 artículos por página (hero + secundario).
  //   'pantalla' → pantalla completa horizontal, grid por sección (hero + secundarios).
  // No afecta la versión impresa (siempre tabloide fijo maquetado).
  const [formatoRevista, setFormatoRevista] = useState(() => {
    try { return localStorage.getItem('news:formatoRevista') || 'a4'; }
    catch { return 'a4'; }
  });
  const cambiarFormato = (nuevo) => {
    setFormatoRevista(nuevo);
    try { localStorage.setItem('news:formatoRevista', nuevo); } catch {}
  };

  // Zoom aplicado al contenedor del flipbook con transform:scale.
  // Vive entre 50% y 200%. Se persiste en localStorage. Con scale != 1
  // el usuario puede arrastrar (pan) para navegar por partes de la página.
  const [zoom, setZoom] = useState(() => {
    try { return Number(localStorage.getItem('news:zoom')) || 1; }
    catch { return 1; }
  });
  const cambiarZoom = (nuevo) => {
    const val = Math.max(0.5, Math.min(2, nuevo));
    setZoom(val);
    if (val <= 1) setPanOffset({ x: 0, y: 0 });
    try { localStorage.setItem('news:zoom', String(val)); } catch {}
  };
  // Offset del pan cuando hay zoom (px)
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const hayMaquetaImpresa = Array.isArray(paginasPdf) && paginasPdf.length > 0;

  // Indice y pasatiempos van en efectos SEPARADOS, cada uno vigilando su
  // propio estado. Juntos no funciona: el indice responde primero y su
  // setState cambia una dependencia del efecto, React ejecuta la limpieza del
  // efecto anterior, y cuando llega la respuesta de los pasatiempos su guardia
  // `vivo` ya es falso y el resultado se descarta en silencio.
  useEffect(() => {
    // edition llega null en el primer render, antes de que cargue la edicion.
    if (!edition?.id || modoRevista !== 'impresa' || indiceImpreso !== null) return;
    let vivo = true;
    maquetaService.indicePublico(edition.id)
      .then(data => { if (vivo) setIndiceImpreso(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setIndiceImpreso([]); });
    return () => { vivo = false; };
  }, [modoRevista, indiceImpreso, edition?.id]);

  useEffect(() => {
    if (!edition?.id || modoRevista !== 'impresa' || interactivos !== null) return;
    let vivo = true;
    maquetaService.interactivosPublicos(edition.id)
      .then(data => { if (vivo) setInteractivos(Array.isArray(data) ? data : []); })
      .catch(() => { if (vivo) setInteractivos([]); });
    return () => { vivo = false; };
  }, [modoRevista, interactivos, edition?.id]);

  // Al cambiar de pagina se cierra el juego: el lector vuelve a ver el papel.
  useEffect(() => { setJugando(false); }, [currentPage, modoRevista]);

  // Pautas publicitarias activas para esta edición, para intercalar entre
  // páginas de artículos del modo web. Si no hay, no aparece nada.
  const [pautasActivas, setPautasActivas] = useState([]);

  // Si el usuario tenía guardado modo='impresa' en localStorage pero esta
  // edición no tiene maqueta impresa (o el fetch del PDF falló), volvemos
  // automáticamente a modo web. Sin esto el visor queda en blanco porque
  // el useMemo de pages devolvía [] al no encontrar paginasPdf.
  useEffect(() => {
    if (modoRevista === 'impresa' && !cargandoPdf && (!paginasPdf || paginasPdf.length === 0)) {
      setModoRevista('web');
      try { localStorage.setItem('news:modoRevista', 'web'); } catch {}
    }
  }, [modoRevista, cargandoPdf, paginasPdf]);

  // Carga la edición
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await editionService.getPublicById(id);
        if (!cancelled) setEdition(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Error cargando edición');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Si la edición tiene maqueta impresa, cargamos su PDF y lo renderizamos
  // como imágenes con pdf.js. Cada imagen se convertirá en una página del
  // flipbook (WYSIWYG con el impreso). pdf.js se importa dinámicamente
  // para no penalizar a las ediciones que no usan maqueta.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Revocar blob URLs anteriores para liberar memoria (si venimos de
      // otra edición o de un reintento).
      if (Array.isArray(paginasPdf)) {
        for (const u of paginasPdf) { try { URL.revokeObjectURL(u); } catch {} }
      }
      if (!id) return;
      try {
        const info = await maquetaService.infoPublica(id).catch((err) => {
          console.warn('[MagazineView] infoPublica falló:', err);
          return null;
        });
        if (!info || !info.tieneMaqueta) {
          setPaginasPdf(null); return;
        }

        setCargandoPdf(true);
        // pdf.js 4.x es full ESM. El módulo se importa desde la raíz del
        // paquete (no de /build/pdf que quedó como legacy) y el worker
        // tiene extensión .mjs — con .min.js falla el fetch dinámico.
        const pdfjs = await import('pdfjs-dist');
        pdfjs.GlobalWorkerOptions.workerSrc =
          `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.mjs`;

        // Cache-buster: al entrar al visor, se genera un timestamp que
        // hace la URL única. Sin esto, el navegador puede servir la copia
        // cacheada (max-age del backend) y no ver cambios recientes del
        // editor hasta que expire el TTL. El pdf.js reusa la respuesta
        // durante la sesión actual porque `url` es constante en este scope.
        const urlBase = maquetaService.urlPdfPublico(id);
        const url = urlBase + (urlBase.includes('?') ? '&' : '?') + '_t=' + Date.now();
        // Pasamos el JWT si hay sesión, así el backend reconoce al Editor
        // y sirve el PDF incluso si la edición sigue en borrador.
        const token = localStorage.getItem('newspaperToken');
        const httpHeaders = token ? { Authorization: `Bearer ${token}` } : undefined;
        const pdf = await pdfjs.getDocument({ url, httpHeaders }).promise;
        const imgs = [];
        setProgresoPdf({ actual: 0, total: pdf.numPages });
        // Render a 1.5x scale — buen balance calidad/peso. Cada canvas se
        // convierte a blob URL (mucho más liviano que dataURL — la conversión
        // a base64 inflaba las páginas hasta 8-32MB total del HTML string,
        // que luego se pegaba en innerHTML y bloqueaba el hilo por segundos).
        //
        // Además, precargamos cada imagen en el navegador antes de exponerla
        // al flipbook: cuando el usuario alterne a modo impresa, StPageFlip
        // encontrará los <img> con dimensiones reales al primer render y no
        // medirá pageBox vacío (esa era la razón por la que quedaba en blanco
        // hasta refrescar la página).
        for (let i = 1; i <= pdf.numPages; i++) {
          if (cancelled) return;
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          const canvas = document.createElement('canvas');
          canvas.width  = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d');
          await page.render({ canvasContext: ctx, viewport }).promise;
          const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
          const url  = URL.createObjectURL(blob);
          // Precarga en el cache del navegador
          await new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve();
            img.onerror = () => resolve();
            img.src = url;
          });
          imgs.push(url);
          if (!cancelled) setProgresoPdf({ actual: i, total: pdf.numPages });
        }
        if (!cancelled) setPaginasPdf(imgs);
      } catch (err) {
        console.warn('No se pudo renderizar la maqueta como PDF, cayendo al modo dinámico:', err);
        if (!cancelled) setPaginasPdf(null);
      } finally {
        if (!cancelled) setCargandoPdf(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Pautas activas de la edición para intercalar en la vista revista modo web.
  // Se cargan TODAS las posiciones — el layout de la página de pauta lo damos
  // aquí, no depende del catálogo de posiciones del módulo Publicidad (que es
  // para la maqueta impresa: banner top, sidebar, etc.).
  //
  // Robusto contra backend antiguo: si el endpoint /activas rechaza posicion
  // nula con 400 (versión previa al fix del parámetro nullable), reintentamos
  // consultando las 3 posiciones conocidas del catálogo por separado.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await publicidadService.getPautasActivas(null, id);
        if (!cancelled && Array.isArray(res)) setPautasActivas(res);
      } catch {
        // Fallback: backend antiguo requiere posicion. Consultamos las
        // posiciones conocidas serializadas (no en paralelo para no saturar).
        const acumulado = [];
        for (const pos of ['home_top', 'sidebar', 'entre_articulos', 'revista_pagina']) {
          if (cancelled) return;
          try {
            const r = await publicidadService.getPautasActivas(pos, id);
            if (Array.isArray(r)) acumulado.push(...r);
          } catch { /* silencio si esta posición falla */ }
        }
        if (!cancelled) setPautasActivas(acumulado);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  // Recomputa tamaño al redimensionar o cambiar formato
  useEffect(() => {
    const onResize = () => setViewportSize(computeSize(formatoRevista));
    onResize(); // aplicar al cambiar formato también
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [formatoRevista]);

  // Atajos de zoom: Ctrl/Cmd + [+/-/0] y rueda del ratón con Ctrl.
  //   Ctrl + '+'  → acercar
  //   Ctrl + '-'  → alejar
  //   Ctrl + '0'  → reset al 100%
  //   Ctrl + wheel scroll → acercar/alejar en el sentido del scroll
  useEffect(() => {
    const onKey = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key === '+' || e.key === '=') { e.preventDefault(); cambiarZoom(zoom + 0.15); }
      else if (e.key === '-') { e.preventDefault(); cambiarZoom(zoom - 0.15); }
      else if (e.key === '0') { e.preventDefault(); cambiarZoom(1); }
    };
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      e.preventDefault();
      cambiarZoom(zoom + (e.deltaY < 0 ? 0.1 : -0.1));
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('wheel', onWheel);
    };
  }, [zoom]); // eslint-disable-line react-hooks/exhaustive-deps

  // Suprime el error asíncrono "Cannot read properties of undefined
  // (reading 'startTime')" que StPageFlip lanza cuando se destruye durante
  // una animación (deja un requestAnimationFrame pendiente que se dispara
  // sobre la instancia ya limpia). El error es cosmético — cuando llega ya
  // creamos la nueva instancia — pero puede ensuciar la consola y llegar
  // a atrapar handlers de error del framework.
  useEffect(() => {
    const onError = (e) => {
      const msg = e?.message || e?.error?.message || '';
      if (msg.includes("reading 'startTime'") || msg.includes('reportAllChanges')) {
        e.preventDefault?.();
        e.stopPropagation?.();
        return true;
      }
    };
    window.addEventListener('error', onError, true);
    return () => window.removeEventListener('error', onError, true);
  }, []);

  // Construye las páginas a partir de los artículos.
  // Si el usuario eligió "impresa" y hay maqueta disponible, cada página del
  // flipbook es una imagen del PDF renderizado por pdf.js. En cualquier otro
  // caso (modo 'web', o modo 'impresa' pero sin maqueta) usa el flow por
  // categorías construido dinámicamente.
  const pages = useMemo(() => {
    if (modoRevista === 'impresa' && hayMaquetaImpresa) {
      return paginasPdf.map((src, i) => ({ type: 'pdf-image', src, index: i }));
    }
    if (!edition) return [];
    return buildPages(edition, formatoRevista, pautasActivas);
  }, [edition, paginasPdf, modoRevista, hayMaquetaImpresa, formatoRevista, pautasActivas]);

  // Inicializa PageFlip cuando el DOM está listo y hay páginas.
  //
  // IMPORTANTE: NO renderizamos las páginas como React children del
  // bookRef. StPageFlip mueve físicamente esos elementos a su propio
  // árbol DOM interno, y luego React (al cleanup / re-render) intenta
  // hacer removeChild sobre nodos que ya no son sus hijos → crash
  // "Failed to execute 'removeChild' on 'Node'".
  //
  // Solución: React deja bookRef vacío. Nosotros generamos el HTML
  // de cada página con renderToStaticMarkup (React DOM Server) y lo
  // inyectamos por innerHTML. StPageFlip toma control total del DOM
  // sin que React tenga fibers que rastrear.
  //
  // El único inconveniente: los onClick del índice no funcionan
  // porque renderToStaticMarkup no serializa handlers. Los reemplazo
  // por data-goto="N" y un event listener delegado en bookRef.
  useEffect(() => {
    if (!bookRef.current || pages.length === 0) return;

    setReiniciandoFlipbook(true);

    // Destruir instancia previa (viewport, edition o modo cambiaron).
    if (pageFlipRef.current) {
      try {
        // Cancelar cualquier animación en curso antes de destruir. Si
        // destruimos mientras hay un flip animándose, StPageFlip deja un
        // requestAnimationFrame pendiente que después dispara
        // "Cannot read properties of undefined (reading 'startTime')"
        // y corrompe la próxima inicialización.
        try { pageFlipRef.current.turnToPage(pageFlipRef.current.getCurrentPageIndex() || 0); } catch (_) {}
        pageFlipRef.current.destroy();
      } catch (_) {}
      pageFlipRef.current = null;
    }
    // Limpiar HTML previo antes de inyectar el nuevo. Con esto
    // StPageFlip empieza desde cero cada vez.
    bookRef.current.innerHTML = '';

    // Flag para cancelar el setup si el efecto se re-dispara (cambio de modo
    // rápido, StrictMode). Se lee dentro del IIFE async antes de cada paso.
    let cancelled = false;

    (async () => {
      try {
        // Descartar rAF pendientes del PageFlip viejo (evita el error
        // async "reading 'startTime'" que corrompe el próximo setup).
        await new Promise(r => setTimeout(r, 50));
        if (cancelled || !bookRef.current) return;
      // Serializar las páginas a HTML puro. renderToStaticMarkup
      // convierte JSX a string sin bindings de React ni handlers.
      // Insertamos una página en blanco al inicio para que la pág 1
      // quede a la DERECHA en el primer spread (imposición tabloide).
      const blankPage = `<div class="mag-page ${styles.page}" style="position:relative;background:#faf9f5;"><img src="${logoImg}" alt="Logo" style="position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);max-width:60%;max-height:60%;object-fit:contain;" /></div>`;
      const htmlPages = pages.map((page, i) => {
        const inner = renderToStaticMarkup(
          renderPage(page, i, edition, pages, styles)
        );
        return `<div class="mag-page ${styles.page}">${inner}</div>`;
      });
      bookRef.current.innerHTML = blankPage + htmlPages.join('');

      // Esperar que las imágenes estén decodificadas ANTES de instanciar
      // PageFlip. Si no, StPageFlip mueve el DOM a su árbol interno cuando
      // las <img> aún tienen dimensiones cero → mide las páginas vacías → se
      // ven en blanco. Con img.decode() forzamos que el navegador tenga la
      // imagen pintable antes de continuar.
      const imgs = Array.from(bookRef.current.querySelectorAll('img'));
      if (imgs.length > 0) {
        await Promise.all(imgs.map(img =>
          img.complete
            ? Promise.resolve()
            : (img.decode ? img.decode().catch(() => {}) : new Promise(r => {
                img.onload = r; img.onerror = r;
              }))
        ));
        // Forzar reflow para que el layout se estabilice
        // eslint-disable-next-line no-unused-expressions
        bookRef.current.offsetHeight;
      }
      if (cancelled || !bookRef.current) {
        return;
      }

      const { width, height, singlePage } = viewportSize;

      // StPageFlip necesita position:relative y dimensiones para anclar
      // sus absolute correctamente. SIN overflow:hidden — lo corta durante el curl.
      const bookW = singlePage ? width : width * 2;
      bookRef.current.style.position = 'relative';
      bookRef.current.style.width    = `${bookW}px`;
      bookRef.current.style.height   = `${height}px`;

      const pf = new PageFlip(bookRef.current, {
        width,
        height,
        size: 'fixed',
        maxShadowOpacity: 0.4,
        showCover: false,
        mobileScrollSupport: false,
        usePortrait: singlePage,
        startPage: 0,
        drawShadow: true,
        flippingTime: 700,
        autoSize: false,
        swipeDistance: 30,
        clickEventForward: true,
        useMouseEvents: true,
        showPageCorners: true,
        disableFlipByClick: false,
      });

      const mgs = bookRef.current.querySelectorAll('.mag-page');
      pf.loadFromHTML(mgs);

    // stageRef: siempre overflow:hidden. Le damos padding lateral = pageWidth
    // para que el canvas de staging de PageFlip quepa sin salirse.
    // El padding no afecta la posición visual del libro porque bookRef
    // usa position:relative dentro del stageRef.
      if (stageRef.current) {
        stageRef.current.style.width      = `${bookW + width * 2}px`;
        stageRef.current.style.height     = `${height}px`;
        stageRef.current.style.overflow   = 'hidden';
        stageRef.current.style.paddingLeft = `${width}px`;
        stageRef.current.style.boxSizing  = 'border-box';
      }

      setTotalPages(pf.getPageCount() - 1); // -1 por la página en blanco inicial
      setCurrentPage(0);

      pf.on('flip', (e) => {
        // e.data es el índice real de StPageFlip (0 = blanco, 1 = portada…)
        // Guardamos el índice real menos 1 para que currentPage==0 sea la portada
        setCurrentPage(Math.max(0, e.data - 1));
      });

      // Arrancar en la portada (índice 1, saltando el blanco)
      setTimeout(() => { try { pf.flip(1); } catch {} }, 80);

      pageFlipRef.current = pf;

      // Ya está listo — quitar el overlay de "preparando".
      setReiniciandoFlipbook(false);
      // Event delegation para clicks en el índice (data-goto="N").
      const onClickInBook = (ev) => {
        const target = ev.target.closest('[data-goto]');
        if (target) {
          const n = parseInt(target.getAttribute('data-goto'), 10);
          if (!Number.isNaN(n)) pf.flip(n);
        }
      };
      bookRef.current.addEventListener('click', onClickInBook);
      cleanupRef.current = { pf, onClickInBook, node: bookRef.current };
      } catch (err) {
        console.error('[MagazineView] flipbook: EXCEPTION', err);
        setReiniciandoFlipbook(false);
      }
    })();

    return () => {
      cancelled = true;
      const c = cleanupRef.current;
      if (c) {
        try { c.node?.removeEventListener('click', c.onClickInBook); } catch (_) {}
        try { c.pf?.destroy(); } catch (_) {}
        try { if (c.node) c.node.innerHTML = ''; } catch (_) {}
        cleanupRef.current = null;
      }
      pageFlipRef.current = null;
    };
  }, [pages, viewportSize, edition, modoRevista]);

  const [navOpen, setNavOpen] = useState(false);

  const goPrev = () => pageFlipRef.current?.flipPrev();
  const goNext = () => pageFlipRef.current?.flipNext();
  // El flipbook lleva una página en blanco insertada al inicio (para que la
  // pág. 1 quede a la derecha del primer pliego, como en la imposición real).
  // Por eso el índice que entiende StPageFlip es el de la página + 1.
  const HOJA_BLANCA = 1;
  const goToPage = (indiceEnPaginas) => {
    pageFlipRef.current?.flip(indiceEnPaginas + HOJA_BLANCA);
    setNavOpen(false);
  };
  // currentPage ya viene compensado desde el handler de 'flip'
  // (setCurrentPage(e.data - 1)), así que es directamente el índice dentro del
  // array de páginas. Solo faltaba compensar en el sentido contrario, al
  // navegar: eso es lo que hace goToPage.
  const paginaActual = currentPage;
  const close = () => navigate(-1);

  if (loading || cargandoPdf) {
    return (
      <div className={styles.stage}>
        <div className={styles.loading}>
          {cargandoPdf ? 'Preparando páginas maquetadas…' : 'Cargando revista…'}
        </div>
      </div>
    );
  }

  if (error || !edition) {
    return (
      <div className={styles.stage}>
        <div className={styles.loading} style={{ color: '#f66' }}>
          {error || 'Edición no encontrada'}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stage}>
      {/* Topbar */}
      <div className={styles.topbar}>
        <div className={styles.topbarTitle}>
          <BookOpen size={14} style={{ verticalAlign: 'middle', marginRight: 8 }} />
          {edition.title || `Edición #${edition.number}`}
        </div>
        <div className={styles.topbarActions}>
          {/* Selector de formato de canvas — solo visible en modo web,
              cambia el tamaño físico y la distribución de artículos. */}
          {modoRevista === 'web' && (
            <div style={{
              display:'inline-flex', background:'rgba(255,255,255,0.08)',
              borderRadius: 999, padding: 3, marginRight: 8,
            }}>
              {[
                { v: 'a4',       t: 'A4' },
                { v: 'tabloide', t: 'Tabloide' },
                { v: 'pantalla', t: 'Pantalla' },
              ].map(o => (
                <button
                  key={o.v}
                  onClick={() => cambiarFormato(o.v)}
                  title={
                    o.v === 'a4' ? 'Formato A4 · 1 artículo por página'
                    : o.v === 'tabloide' ? 'Tabloide · 2 artículos por página'
                    : 'Pantalla completa · grid por sección'
                  }
                  style={{
                    padding:'6px 10px', borderRadius: 999, border:'none',
                    background: formatoRevista === o.v ? 'rgba(255,255,255,0.18)' : 'transparent',
                    color:'#fff', fontSize:11, fontWeight:600, cursor:'pointer',
                    letterSpacing:'.03em',
                  }}
                >{o.t}</button>
              ))}
            </div>
          )}
          {/* Controles de zoom — visibles siempre (web e impresa).
              El zoom es útil sobre todo en modo impresa para leer detalles
              de la maqueta densa. */}
          <div style={{
            display:'inline-flex', alignItems:'center',
            background:'rgba(255,255,255,0.08)',
            borderRadius: 999, padding: 3, marginRight: 8,
          }}>
            <button
              onClick={() => cambiarZoom(zoom - 0.15)}
              disabled={zoom <= 0.5}
              title="Alejar (–)"
              style={{
                padding:'6px 10px', borderRadius: 999, border:'none',
                background:'transparent', color:'#fff', fontSize:14, fontWeight:600,
                cursor: zoom <= 0.5 ? 'default' : 'pointer',
                opacity: zoom <= 0.5 ? 0.4 : 1,
                lineHeight:1,
              }}
            >−</button>
            <button
              onClick={() => cambiarZoom(1)}
              title="Zoom al 100%"
              style={{
                padding:'6px 10px', borderRadius: 999, border:'none',
                background:'transparent', color:'#fff', fontSize:11, fontWeight:600,
                cursor:'pointer', minWidth:42,
              }}
            >{Math.round(zoom * 100)}%</button>
            <button
              onClick={() => cambiarZoom(zoom + 0.15)}
              disabled={zoom >= 2}
              title="Acercar (+)"
              style={{
                padding:'6px 10px', borderRadius: 999, border:'none',
                background:'transparent', color:'#fff', fontSize:14, fontWeight:600,
                cursor: zoom >= 2 ? 'default' : 'pointer',
                opacity: zoom >= 2 ? 0.4 : 1,
                lineHeight:1,
              }}
            >+</button>
          </div>
          {/* Switch versión web / impresa. */}
          <div style={{
            display:'inline-flex', background:'rgba(255,255,255,0.08)',
            borderRadius: 999, padding: 3, marginRight: 8,
          }}>
            <button
              onClick={() => cambiarModo('web')}
              style={{
                padding:'6px 12px', borderRadius: 999, border:'none',
                background: modoRevista === 'web' ? 'rgba(255,255,255,0.18)' : 'transparent',
                color:'#fff', fontSize:12, fontWeight:600, cursor:'pointer',
                letterSpacing:'.03em',
              }}
            >Versión web</button>
            <button
              onClick={() => hayMaquetaImpresa && cambiarModo('impresa')}
              disabled={!hayMaquetaImpresa}
              title={hayMaquetaImpresa ? 'Ver como se imprimió' : 'Esta edición no tiene maqueta impresa'}
              style={{
                padding:'6px 12px', borderRadius: 999, border:'none',
                background: modoRevista === 'impresa' ? 'rgba(255,255,255,0.18)' : 'transparent',
                color: hayMaquetaImpresa ? '#fff' : 'rgba(255,255,255,0.35)',
                fontSize:12, fontWeight:600,
                cursor: hayMaquetaImpresa ? 'pointer' : 'not-allowed',
                letterSpacing:'.03em',
              }}
            >Versión impresa</button>
          </div>
          <button className={styles.iconBtn} onClick={() => setNavOpen(o => !o)} title="Contenido">
            <List size={18} />
          </button>
          <button className={styles.iconBtn} onClick={close} title="Cerrar">
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Panel lateral de navegación */}
      {navOpen && (
        <div className={styles.navPanel}>
          <div className={styles.navPanelHeader}>
            <span>Contenido</span>
            <button className={styles.iconBtn} onClick={() => setNavOpen(false)}><X size={16} /></button>
          </div>
          <ul className={styles.navPanelList}>
            <li
              className={`${styles.navPanelItem} ${paginaActual === 0 ? styles.navPanelActive : ''}`}
              onClick={() => goToPage(0)}
            >
              <span className={styles.navPanelPage}>1</span>
              <span className={styles.navPanelTitle}>Portada</span>
            </li>
            {modoRevista === 'impresa' && indiceImpreso === null && (
              <li className={styles.navPanelItem}>
                <span className={styles.navPanelTitle} style={{ fontStyle:'italic', opacity:0.7 }}>
                  Cargando contenido…
                </span>
              </li>
            )}
            {(() => {
              // Modo impreso: la guia se arma con el indice de la maqueta,
              // agrupado por categoria igual que en la version web, y cada
              // entrada salta a la pagina fisica del PDF.
              if (modoRevista === 'impresa') {
                const items = indiceImpreso || [];
                if (items.length === 0) return null;
                const filas = [];
                let ultimaCat = null;
                for (const it of items) {
                  const cat = it.categoria || 'Sin categoría';
                  if (cat !== ultimaCat) {
                    ultimaCat = cat;
                    filas.push(
                      <li key={`cat-${cat}-${it.pagina}`} className={styles.navPanelCatHeader}>
                        {cat}
                      </li>
                    );
                  }
                  const idx = Math.max(0, (it.pagina || 1) - 1);
                  filas.push(
                    <li
                      key={`${it.articuloId}-${it.pagina}`}
                      className={`${styles.navPanelItem} ${paginaActual === idx ? styles.navPanelActive : ''}`}
                      onClick={() => goToPage(idx)}
                    >
                      <span className={styles.navPanelPage}>{it.pagina}</span>
                      <div className={styles.navPanelInfo}>
                        <span className={styles.navPanelTitle}>{it.titulo}</span>
                      </div>
                    </li>
                  );
                }

                // Juegos de la edicion. Van al final de la guia y sirven ademas
                // para ver si el lector los esta recibiendo: si la lista llega
                // vacia, aqui queda dicho, en vez de no aparecer nada en ningun
                // lado y tener que mirar la consola.
                filas.push(
                  <li key="hdr-juegos" className={styles.navPanelCatHeader}>MULTIMEDIA Y PASATIEMPOS</li>
                );
                if (interactivos === null) {
                  filas.push(
                    <li key="juegos-cargando" className={styles.navPanelItem}>
                      <span className={styles.navPanelTitle} style={{ fontStyle:'italic', opacity:.7 }}>
                        Buscando…
                      </span>
                    </li>
                  );
                } else if (interactivos.length === 0) {
                  filas.push(
                    <li key="juegos-vacio" className={styles.navPanelItem}>
                      <span className={styles.navPanelTitle} style={{ fontStyle:'italic', opacity:.7 }}>
                        Esta edición no tiene audios, videos ni pasatiempos
                      </span>
                    </li>
                  );
                } else {
                  for (const j of interactivos) {
                    const idxJ = Math.max(0, (j.pagina || 1) - 1);
                    const esPieza = j.tipoContenido === 'multimedia';
                    const rotulo = esPieza
                      ? `${j.datos?.tipo === 'video' ? '▶' : '♪'} ${j.datos?.titulo || 'Pieza multimedia'}`
                      : '🔤 Sopa de letras — jugar';
                    filas.push(
                      <li
                        key={`inter-${j.tipoContenido}-${j.pagina}`}
                        className={styles.navPanelItem}
                        onClick={() => { goToPage(idxJ); setJugando(true); setNavOpen(false); }}
                      >
                        <span className={styles.navPanelPage}>{j.pagina}</span>
                        <div className={styles.navPanelInfo}>
                          <span className={styles.navPanelTitle}>{rotulo}</span>
                        </div>
                      </li>
                    );
                  }
                }
                return filas;
              }

              const arts = pages._articles || [];
              const rendered = [];
              let lastCat = null;
              for (const art of arts) {
                const catName = art.category?.name || 'Sin categoría';
                if (catName !== lastCat) {
                  lastCat = catName;
                  rendered.push(
                    <li key={`cat-${catName}`} className={styles.navPanelCatHeader}>
                      {catName}
                    </li>
                  );
                }
                const pageIdx = pages._articleOffsets?.[art.id] ?? 0;
                rendered.push(
                  <li
                    key={art.id}
                    className={`${styles.navPanelItem} ${paginaActual === pageIdx ? styles.navPanelActive : ''}`}
                    onClick={() => goToPage(pageIdx)}
                  >
                    <span className={styles.navPanelPage}>{pageIdx + 1}</span>
                    <div className={styles.navPanelInfo}>
                      <span className={styles.navPanelTitle}>
                        {art.isCover ? '⭐ ' : ''}{art.title}
                      </span>
                    </div>
                  </li>
                );
              }
              return rendered;
            })()}
          </ul>
        </div>
      )}

      {/* Sopa de letras jugable sobre la página impresa donde está. El lector
          ve el papel y, si quiere, la resuelve encima sin salir de la revista. */}
      {(() => {
        if (modoRevista !== 'impresa') return null;
        // El flipbook muestra dos paginas por pliego, asi que currentPage puede
        // ser la izquierda mientras la sopa esta en la derecha. Se acepta la
        // pagina y su vecina en vez de exigir coincidencia exacta.
        // Pieza multimedia en esta página: el botón abre el reproductor.
        const pieza = (interactivos || []).find(
          x => x.tipoContenido === 'multimedia'
            && Math.abs((x.pagina - 1) - paginaActual) <= 1
        );
        if (pieza) {
          if (jugando) {
            return <ReproductorPieza datos={pieza.datos} onCerrar={() => setJugando(false)} />;
          }
          return (
            <button
              onClick={() => setJugando(true)}
              style={{
                position:'absolute', left:'50%', bottom:24, transform:'translateX(-50%)',
                zIndex:15, padding:'10px 20px', borderRadius:24, border:'none',
                background:'#c9a227', color:'#1a1a1a', fontSize:14, fontWeight:700,
                cursor:'pointer', boxShadow:'0 4px 16px rgba(0,0,0,.4)',
              }}
            >
              {pieza.datos?.tipo === 'video' ? '▶ Ver el video' : '▶ Escuchar el audio'}
            </button>
          );
        }

        const lista = (interactivos || []).filter(x => x.tipoContenido === 'sopa_letras');
        if (lista.length === 0) return null;
        // En la pagina donde esta impresa, o la vecina del pliego. Si el lector
        // entro desde la guia de contenido, se abre la que eligio.
        const sopa = lista.find(x => Math.abs((x.pagina - 1) - paginaActual) <= 1)
                  || (jugando ? lista[0] : null);
        if (!sopa) return null;
        if (jugando) {
          return <SopaInteractiva datos={sopa.datos} onCerrar={() => setJugando(false)} />;
        }
        return (
          <button
            onClick={() => setJugando(true)}
            style={{
              position:'absolute', left:'50%', bottom:24, transform:'translateX(-50%)',
              zIndex:15, padding:'10px 20px', borderRadius:24, border:'none',
              background:'#c9a227', color:'#1a1a1a', fontSize:14, fontWeight:700,
              cursor:'pointer', boxShadow:'0 4px 16px rgba(0,0,0,.4)',
            }}
          >
            Resolver la sopa aquí
          </button>
        );
      })()}

      {/* Flechas */}
      <button
        className={`${styles.navArrow} ${styles.navPrev}`}
        onClick={goPrev}
        disabled={currentPage === 0}
        aria-label="Página anterior"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        className={`${styles.navArrow} ${styles.navNext}`}
        onClick={goNext}
        disabled={currentPage >= totalPages - 1}
        aria-label="Página siguiente"
      >
        <ChevronRight size={24} />
      </button>

      {/* stageRef: wrapper con overflow:hidden que clipea exactamente el spread.
          bookRef: contenedor que PageFlip controla (position:relative, sin overflow).
          El zoomWrapper envuelve al stage con transform:scale + translate para
          permitir zoom y pan sin tocar StPageFlip (que se rompe si le cambias
          dimensiones on-the-fly). */}
      <div className={styles.bookWrapper} style={{ position: 'relative' }}>
        <div
          onDoubleClick={(e) => {
            // Doble-click alterna 100% ↔ 150%. Si ya está zoomeado, vuelve
            // al 100%; si no, hace zoom al 150% y centra en el punto clicado.
            if (zoom > 1) { cambiarZoom(1); return; }
            cambiarZoom(1.5);
          }}
          onMouseDown={(e) => {
            if (zoom <= 1) return; // sin pan si no hay zoom
            const startX = e.clientX - panOffset.x;
            const startY = e.clientY - panOffset.y;
            const onMove = (ev) => setPanOffset({ x: ev.clientX - startX, y: ev.clientY - startY });
            const onUp   = () => {
              document.removeEventListener('mousemove', onMove);
              document.removeEventListener('mouseup', onUp);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
          }}
          style={{
            transform: `translate(${panOffset.x}px, ${panOffset.y}px) scale(${zoom})`,
            transformOrigin: 'center center',
            transition: 'transform .12s ease-out',
            cursor: zoom > 1 ? 'grab' : 'default',
            userSelect: zoom > 1 ? 'none' : 'auto',
          }}
        >
          <div
            ref={stageRef}
            style={{ overflow: 'hidden', position: 'relative', flexShrink: 0 }}
          >
            <div ref={bookRef} className={styles.book} key={`${modoRevista}-${formatoRevista}`} />
          </div>
        </div>

        {/* Overlay unificado de estados de carga.
            (a) Descargando y renderizando el PDF impreso — muestra progreso X/N.
            (b) Reinicializando el flipbook al cambiar de modo (web↔impresa)
                o al resize del viewport. */}
        {(cargandoPdf || reiniciandoFlipbook) && (
          <div style={{
            position:'absolute', inset:0,
            background:'rgba(15,20,25,0.75)', color:'#f5efe4',
            display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center',
            gap:12, zIndex:20, backdropFilter:'blur(2px)',
          }}>
            <div style={{
              width:40, height:40, borderRadius:'50%',
              border:'3px solid rgba(255,255,255,0.15)',
              borderTopColor:'#f5efe4',
              animation:'spin 0.8s linear infinite',
            }} />
            <div style={{ fontSize:14, fontWeight:600, letterSpacing:'.02em' }}>
              {cargandoPdf
                ? (progresoPdf.total > 0
                    ? `Preparando maqueta impresa · ${progresoPdf.actual} / ${progresoPdf.total}`
                    : 'Descargando maqueta impresa…')
                : 'Cargando revista…'}
            </div>
            {cargandoPdf && progresoPdf.total > 0 && (
              <div style={{
                width:240, height:4, background:'rgba(255,255,255,0.15)', borderRadius:2, overflow:'hidden',
              }}>
                <div style={{
                  width: `${(progresoPdf.actual / progresoPdf.total) * 100}%`,
                  height:'100%', background:'#f5efe4', transition:'width .2s',
                }} />
              </div>
            )}
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        )}
      </div>

      {/* Dock inferior: contador de página con flechas grandes.
          Reemplaza la barra de miniaturas anterior (que era confusa
          con muchas páginas) por un control minimalista al estilo
          de lectores tipo Kindle/Issuu. */}
      <div className={styles.dock}>
        <button
          className={styles.dockBtn}
          onClick={goPrev}
          disabled={currentPage === 0}
          aria-label="Página anterior"
        >
          <ChevronLeft size={18} />
        </button>
        <div className={styles.dockCounter}>
          <span className={styles.dockNum}>{currentPage + 1}</span>
          <span className={styles.dockSep}> / </span>
          <span className={styles.dockTotal}>{totalPages}</span>
        </div>
        <button
          className={styles.dockBtn}
          onClick={goNext}
          disabled={currentPage >= totalPages - 1}
          aria-label="Página siguiente"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// Construcción de páginas
// ────────────────────────────────────────────────────────────────

/**
 * Convierte la edición en un array de "páginas" a renderizar.
 * Cada página tiene shape { type, hard, ...datos }.
 *
 * types:
 *   'cover'        Portada
 *   'toc'          Índice (uno o dos según cantidad de artículos)
 *   'article'      Artículo (posiblemente parte de uno más largo)
 *   'back'         Contraportada
 */
function buildPages(edition, formato = 'a4', pautas = []) {
  // Separamos las pautas según su slug_posicion del catálogo:
  //   · home_top          → banner al pie de la portada
  //   · sidebar           → banner al pie del índice
  //   · entre_articulos   → página intersticial entre grupos de artículos
  //   · revista_pagina    → página propia (idem entre_articulos, pero pensada
  //                         para pauta grande de página completa)
  // Cualquier otra posición cae en el bucket intersticial por defecto.
  const pautasByPos = { home_top: [], sidebar: [], intersticial: [] };
  for (const p of pautas) {
    const pos = p.slugPosicion || p.SlugPosicion || p.posicion || '';
    if (pos === 'home_top') pautasByPos.home_top.push(p);
    else if (pos === 'sidebar') pautasByPos.sidebar.push(p);
    else pautasByPos.intersticial.push(p);
  }

  // Portada + índice + lista final de artículos ordenados (portada primero,
  // luego por categoría preservando orden) — común a los tres formatos.
  const rawArticles = edition.articles || [];
  const coverArticle = rawArticles.find(a => a.isCover);
  const rest = rawArticles.filter(a => !a.isCover);

  const categoryOrder = [];
  const byCategory = {};
  for (const art of rest) {
    const catKey = art.category?.id || 'sin-categoria';
    if (!byCategory[catKey]) { byCategory[catKey] = []; categoryOrder.push(catKey); }
    byCategory[catKey].push(art);
  }
  for (const key of categoryOrder) {
    byCategory[key].sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  }
  const articles = [
    ...(coverArticle ? [coverArticle] : []),
    ...categoryOrder.flatMap(k => byCategory[k]),
  ];

  // Cada formato define su distribución de artículos por página.
  if (formato === 'pantalla') {
    return buildPagesPantalla(edition, articles, categoryOrder, byCategory, pautasByPos);
  }
  if (formato === 'tabloide') {
    return buildPagesTabloide(edition, articles, pautasByPos);
  }
  return buildPagesA4(edition, articles, pautasByPos);
}

// ─── Formato A4 — 1 artículo por página (comportamiento original) ────────
function buildPagesA4(edition, articles, pautasByPos) {
  const pages = [];
  pages.push({ type: 'cover', pautasHomeTop: pautasByPos.home_top });

  const chunkedToc = chunkArray(articles, 9);
  const tocPages = chunkedToc.map((chunk, idx) => ({
    type: 'toc', items: chunk, partIndex: idx, totalParts: chunkedToc.length,
    pautasSidebar: idx === 0 ? pautasByPos.sidebar : [],
  }));
  pages.push(...tocPages);

  const articleOffsets = {};
  let pautaIdx = 0;
  const intersticiales = pautasByPos.intersticial;
  articles.forEach((art, i) => {
    articleOffsets[art.id] = pages.length;
    const chunks = chunkArticleText(art.content || art.excerpt || '', CHARS_POR_PAGINA_ARTICULO);
    chunks.forEach((chunk, idx) => {
      pages.push({ type: 'article', article: art, chunkIndex: idx, totalChunks: chunks.length, content: chunk });
    });
    // Cada 3 artículos insertamos una pauta intersticial si hay
    if ((i + 1) % 3 === 0 && intersticiales.length > 0) {
      pages.push({ type: 'pauta', pauta: intersticiales[pautaIdx % intersticiales.length] });
      pautaIdx++;
    }
  });

  tocPages.forEach(p => {
    p.items = p.items.map(a => ({ ...a, _page: articleOffsets[a.id] + 1 }));
  });

  pages.push({ type: 'back' });
  pages._articleOffsets = articleOffsets;
  pages._articles = articles;
  return pages;
}

// ─── Formato Tabloide — 2 artículos por página lado a lado ───────────────
function buildPagesTabloide(edition, articles, pautasByPos) {
  const pages = [];
  pages.push({ type: 'cover', pautasHomeTop: pautasByPos.home_top });

  const chunkedToc = chunkArray(articles, 12);
  const tocPages = chunkedToc.map((chunk, idx) => ({
    type: 'toc', items: chunk, partIndex: idx, totalParts: chunkedToc.length,
    pautasSidebar: idx === 0 ? pautasByPos.sidebar : [],
  }));
  pages.push(...tocPages);

  const articleOffsets = {};
  let pautaIdx = 0;
  const intersticiales = pautasByPos.intersticial;
  // Emparejamos artículos de a 2 (hero + secundario)
  for (let i = 0; i < articles.length; i += 2) {
    const primer = articles[i];
    const segundo = articles[i + 1] || null;
    articleOffsets[primer.id] = pages.length;
    if (segundo) articleOffsets[segundo.id] = pages.length;
    pages.push({
      type: 'tabloide-spread',
      hero: primer,
      secundario: segundo,
    });
    // Cada 4 páginas de contenido, una pauta intersticial
    if ((Math.floor(i / 2) + 1) % 4 === 0 && intersticiales.length > 0) {
      pages.push({ type: 'pauta', pauta: intersticiales[pautaIdx % intersticiales.length] });
      pautaIdx++;
    }
  }

  tocPages.forEach(p => {
    p.items = p.items.map(a => ({ ...a, _page: articleOffsets[a.id] + 1 }));
  });

  pages.push({ type: 'back' });
  pages._articleOffsets = articleOffsets;
  pages._articles = articles;
  return pages;
}

// ─── Formato Pantalla — grid por sección tipo home digital ───────────────
function buildPagesPantalla(edition, articles, categoryOrder, byCategory, pautasByPos) {
  const pages = [];
  pages.push({ type: 'cover', pautasHomeTop: pautasByPos.home_top });
  pages.push({
    type: 'toc', items: articles.slice(0, 20), partIndex: 0, totalParts: 1,
    pautasSidebar: pautasByPos.sidebar,
  });

  const articleOffsets = {};
  let pautaIdx = 0;
  const intersticiales = pautasByPos.intersticial;

  // Primera página home: mezcla de las categorías más importantes.
  const heroArt = articles.find(a => a.isCover) || articles[0];
  const secundarios = articles.filter(a => a.id !== heroArt.id).slice(0, 6);
  articleOffsets[heroArt.id] = pages.length;
  secundarios.forEach(a => { articleOffsets[a.id] = pages.length; });
  pages.push({
    type: 'section-home',
    titulo: 'Portada',
    hero: heroArt,
    secundarios,
    esPortada: true,
  });

  if (intersticiales.length > 0) {
    pages.push({ type: 'pauta', pauta: intersticiales[pautaIdx % intersticiales.length] });
    pautaIdx++;
  }

  // Después una página por categoría con hero + 4 secundarios.
  for (const catKey of categoryOrder) {
    const arts = byCategory[catKey];
    if (!arts || arts.length === 0) continue;
    const catNombre = arts[0].category?.name || 'Sección';
    for (let i = 0; i < arts.length; i += 5) {
      const grupo = arts.slice(i, i + 5);
      const heroG = grupo[0];
      const secG = grupo.slice(1);
      articleOffsets[heroG.id] = pages.length;
      secG.forEach(a => { articleOffsets[a.id] = pages.length; });
      pages.push({
        type: 'section-home',
        titulo: catNombre,
        hero: heroG,
        secundarios: secG,
      });
    }
    if (intersticiales.length > 0) {
      pages.push({ type: 'pauta', pauta: intersticiales[pautaIdx % intersticiales.length] });
      pautaIdx++;
    }
  }

  pages.forEach(p => {
    if (p.type === 'toc') {
      p.items = p.items.map(a => ({ ...a, _page: (articleOffsets[a.id] ?? 0) + 1 }));
    }
  });

  pages.push({ type: 'back' });
  pages._articleOffsets = articleOffsets;
  pages._articles = articles;
  return pages;
}

function chunkArray(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out.length === 0 ? [[]] : out;
}

/**
 * Divide el HTML/texto del artículo en trozos que quepan en una
 * página. Estrategia simple: cortar por párrafos hasta acercarse al
 * límite de caracteres, sin romper párrafos por la mitad.
 */
function chunkArticleText(html, maxChars) {
  if (!html) return [''];
  // Separar por párrafos <p>...</p> o por dobles saltos
  let paragraphs;
  if (/<p[\s>]/i.test(html)) {
    paragraphs = html.match(/<p[\s\S]*?<\/p>/gi) || [html];
  } else {
    paragraphs = html.split(/\n\s*\n/).filter(Boolean).map(p =>
      /<[a-z]/i.test(p) ? p : `<p>${p}</p>`);
  }

  const chunks = [];
  let current = '';
  for (const p of paragraphs) {
    const plainLen = p.replace(/<[^>]+>/g, '').length;
    if (current && (current.replace(/<[^>]+>/g, '').length + plainLen) > maxChars) {
      chunks.push(current);
      current = p;
    } else {
      current += p;
    }
  }
  if (current) chunks.push(current);
  return chunks.length ? chunks : [''];
}

// ────────────────────────────────────────────────────────────────
// Renderizado de página
// ────────────────────────────────────────────────────────────────

function renderPage(page, i, edition, allPages, s) {
  const folio = <div className={s.folio}>{i + 1}</div>;

  // Maqueta impresa: cada página es una imagen del PDF renderizado.
  if (page.type === 'pdf-image') {
    return (
      <div className={s.pageInner} style={{
        padding: 0, background: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <img
          src={page.src}
          alt={`Página ${i + 1}`}
          style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
        />
      </div>
    );
  }

  if (page.type === 'cover') return renderCover(edition, s, page.pautasHomeTop);
  if (page.type === 'back')  return renderBack(edition, s);
  if (page.type === 'toc')   return (
    <div className={s.pageInner} style={{ position:'relative', paddingBottom: (page.pautasSidebar?.length ? 130 : undefined) }}>
      {renderToc(page, edition, s)}
      {page.pautasSidebar?.length > 0 && (
        <div style={{ position:'absolute', left:0, right:0, bottom:0 }}>
          <PautaCarrusel pautas={page.pautasSidebar} />
        </div>
      )}
      {folio}
    </div>
  );
  if (page.type === 'article') return (
    <div className={s.pageInner}>
      {renderArticle(page, s)}
      {folio}
    </div>
  );
  if (page.type === 'tabloide-spread') return (
    <div className={s.pageInner}>
      {renderTabloideSpread(page, s)}
      {folio}
    </div>
  );
  if (page.type === 'section-home') return (
    <div className={s.pageInner}>
      {renderSectionHome(page, s)}
      {folio}
    </div>
  );
  if (page.type === 'pauta') return (
    <div className={s.pageInner}>
      {renderPautaPage(page, s)}
      {folio}
    </div>
  );
  return null;
}

// Layout tabloide: hero grande a la izquierda (60%), un secundario más
// compacto a la derecha (40%). Ambos con imagen, título, bajada y firma.
function renderTabloideSpread(page, s) {
  const { hero, secundario } = page;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: 24, height: '100%', padding: '32px 32px 60px' }}>
      <article style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
        {hero.category?.name && (
          <div style={{ fontSize: 10, letterSpacing: 2, fontWeight: 700, color: '#c9a227', marginBottom: 6, textTransform: 'uppercase' }}>
            {hero.category.name}
          </div>
        )}
        <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 26, fontWeight: 700, color: '#1B4332', lineHeight: 1.1, margin: '0 0 10px' }}>
          {hero.title}
        </h2>
        {hero.excerpt && (
          <p style={{ fontStyle: 'italic', fontSize: 13, color: '#4a5555', lineHeight: 1.4, margin: '0 0 12px' }}>
            {hero.excerpt}
          </p>
        )}
        {hero.heroImageUrl && (
          <div style={{ width: '100%', height: 220, overflow: 'hidden', marginBottom: 10, background: '#eee' }}>
            <img src={hero.heroImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}
        <div style={{ fontSize: 12, lineHeight: 1.55, color: '#333', flex: 1, overflow: 'hidden' }}>
          {(hero.content || hero.excerpt || '').substring(0, 900)}…
        </div>
      </article>
      {secundario && (
        <article style={{ display: 'flex', flexDirection: 'column', borderLeft: '1px solid #e0d9c7', paddingLeft: 20, overflow: 'hidden', minWidth: 0 }}>
          {secundario.category?.name && (
            <div style={{ fontSize: 9, letterSpacing: 1.8, fontWeight: 700, color: '#c9a227', marginBottom: 4, textTransform: 'uppercase' }}>
              {secundario.category.name}
            </div>
          )}
          <h3 style={{ fontFamily: 'Georgia, serif', fontSize: 18, fontWeight: 700, color: '#1B4332', lineHeight: 1.15, margin: '0 0 8px' }}>
            {secundario.title}
          </h3>
          {secundario.heroImageUrl && (
            <div style={{ width: '100%', height: 140, overflow: 'hidden', marginBottom: 8, background: '#eee' }}>
              <img src={secundario.heroImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          {secundario.excerpt && (
            <p style={{ fontStyle: 'italic', fontSize: 11.5, color: '#4a5555', lineHeight: 1.4, margin: '0 0 8px' }}>
              {secundario.excerpt}
            </p>
          )}
          <div style={{ fontSize: 11.5, lineHeight: 1.5, color: '#444', flex: 1, overflow: 'hidden' }}>
            {(secundario.content || secundario.excerpt || '').substring(0, 500)}…
          </div>
        </article>
      )}
    </div>
  );
}

// Layout home/sección: hero grande arriba con imagen prominente + grid de
// secundarios (2 columnas × 2-3 filas) debajo. Título de sección arriba.
function renderSectionHome(page, s) {
  const { titulo, hero, secundarios = [], esPortada } = page;
  return (
    <div style={{ padding: '28px 40px 60px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ borderBottom: '2px solid #1B4332', paddingBottom: 8, marginBottom: 16 }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: '#c9a227', fontWeight: 700 }}>
          {esPortada ? 'PORTADA' : 'SECCIÓN'}
        </div>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, margin: '2px 0 0', color: '#1B4332', fontWeight: 700 }}>
          {titulo}
        </h1>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Hero grande */}
        <article style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
          {hero.heroImageUrl && (
            <div style={{ width: '100%', height: 260, overflow: 'hidden', marginBottom: 12, background: '#eee' }}>
              <img src={hero.heroImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          )}
          {hero.category?.name && (
            <div style={{ fontSize: 10, letterSpacing: 2, fontWeight: 700, color: '#c9a227', marginBottom: 4, textTransform: 'uppercase' }}>
              {hero.category.name}
            </div>
          )}
          <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 24, color: '#1B4332', lineHeight: 1.1, margin: '0 0 8px', fontWeight: 700 }}>
            {hero.title}
          </h2>
          {hero.excerpt && (
            <p style={{ fontStyle: 'italic', fontSize: 12.5, color: '#4a5555', lineHeight: 1.4, margin: 0 }}>
              {hero.excerpt}
            </p>
          )}
        </article>
        {/* Grid de secundarios */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, overflow: 'hidden' }}>
          {secundarios.slice(0, 4).map((art, k) => (
            <article key={art.id || k} style={{ display: 'flex', gap: 10, borderBottom: '1px solid #e0d9c7', paddingBottom: 10, minWidth: 0 }}>
              {art.heroImageUrl && (
                <div style={{ width: 90, height: 70, overflow: 'hidden', background: '#eee', flexShrink: 0 }}>
                  <img src={art.heroImageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                {art.category?.name && (
                  <div style={{ fontSize: 8, letterSpacing: 1.5, fontWeight: 700, color: '#c9a227', marginBottom: 3, textTransform: 'uppercase' }}>
                    {art.category.name}
                  </div>
                )}
                <h4 style={{ fontFamily: 'Georgia, serif', fontSize: 13, margin: 0, color: '#1B4332', lineHeight: 1.2, fontWeight: 700 }}>
                  {art.title}
                </h4>
              </div>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

// Página de pauta publicitaria: imagen (o video/html) centrada con banner
// "PUBLICIDAD" arriba. Si es imagen la mostramos a tamaño máximo respetando
// aspect ratio; el clic abre urlDestino.
function renderPautaPage(page, s) {
  const p = page.pauta;
  if (!p) return null;
  return (
    <div style={{ padding: '40px 40px 60px', height: '100%', display: 'flex', flexDirection: 'column', background: '#faf7f0' }}>
      <div style={{ fontSize: 10, letterSpacing: 4, color: '#888', textAlign: 'center', marginBottom: 20, fontWeight: 600 }}>
        PUBLICIDAD
      </div>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 0, overflow: 'hidden' }}>
        {p.tipoArchivo === 'imagen' && p.urlStorage ? (
          <a href={p.urlDestino || '#'} target="_blank" rel="noopener noreferrer" style={{ display: 'block', maxWidth: '100%', maxHeight: '100%' }}>
            <img src={p.urlStorage} alt="Publicidad"
              style={{ display: 'block', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }} />
          </a>
        ) : p.tipoArchivo === 'video' && p.urlStorage ? (
          <video src={p.urlStorage} controls autoPlay muted loop playsInline
            style={{ maxWidth: '100%', maxHeight: '100%' }} />
        ) : (
          <div style={{ color: '#999', fontSize: 14, fontStyle: 'italic' }}>
            {p.nombre || 'Espacio publicitario'}
          </div>
        )}
      </div>
      {p.anunciante && (
        <div style={{ fontSize: 11, color: '#666', textAlign: 'center', marginTop: 16 }}>
          {p.anunciante}
        </div>
      )}
    </div>
  );
}

function renderCover(edition, s, pautasHomeTop) {
  const coverUrl = edition.coverUrl || edition.pdfUrl || '';
  const style = coverUrl ? { '--cover-image': `url("${coverUrl}")` } : {};
  const fecha = edition.publishedAt || edition.date;
  return (
    <div style={{
      width:'100%', height:'100%', background:'#f5efe4',
      display:'flex', flexDirection:'column',
    }}>
      <div className={s.coverPage} style={{ ...style, flex:'1 1 auto', position:'relative' }}>
        <div className={s.coverContent}>
          <div className={s.coverKicker}>Edición Digital</div>
          <div className={s.coverBrand}>Comercio<br/>Colaborativo</div>
          <div className={s.coverBrandLine} />
          <div className={s.coverEdicion}>Nº {edition.number}</div>
          <div className={s.coverFecha}>
            {fecha ? new Date(fecha).toLocaleDateString('es-CO', {
              year: 'numeric', month: 'long', day: 'numeric',
            }) : ''}
          </div>
        </div>
        <div className={s.coverHint}>Toque para abrir →</div>
      </div>
      {pautasHomeTop?.length > 0 && (
        <div style={{ padding:'14px 20px 20px', flexShrink:0 }}>
          <PautaCarrusel pautas={pautasHomeTop} />
        </div>
      )}
    </div>
  );
}

/**
 * PautaCarrusel — rota N pautas con opacity CSS pura, sin JS.
 *
 * Cada pauta se apila con position:absolute y recibe una animation con
 * animation-delay escalonado: la i-ésima aparece a los i*ciclo segundos.
 * Duración total = N * ciclo. Cuando hay UNA sola pauta, no anima —
 * simplemente se muestra fija (evita el flash inicial de la animación).
 *
 * IMPORTANTE: como el visor de revista serializa este componente con
 * renderToStaticMarkup y lo inyecta con innerHTML, cualquier lógica
 * React (useState, useEffect) NO se ejecutaría. Por eso todo el
 * carrusel es CSS puro con @keyframes inline.
 */
function PautaCarrusel({ pautas }) {
  if (!pautas || pautas.length === 0) return null;
  const CICLO_SEG = 6; // cada pauta visible ~5s + 1s de transición
  const total = pautas.length;
  const totalSeg = total * CICLO_SEG;

  // ID único por render para que los @keyframes no colisionen si hay
  // varios carruseles en la misma página (portada + índice, por ej.).
  const nonce = Math.random().toString(36).slice(2, 8);
  const animName = `pauta-fade-${nonce}`;
  const contenedorId = `pauta-carrusel-${nonce}`;

  // Un solo item → sin animación (más limpio visualmente).
  if (total === 1) {
    return (
      <div style={wrapCarruselStyle(false)}>
        <div style={kickerStyle}>PUBLICIDAD</div>
        <div style={itemsWrapStyle}>
          <div style={{ position:'relative', width:'100%', height:'100%' }}>
            {renderPautaContenido(pautas[0])}
          </div>
        </div>
      </div>
    );
  }

  // Fórmula del %: cada pauta se muestra el (100/N)% del tiempo total,
  // con un pequeño fade in/out en los bordes.
  const pctVisible = 100 / total;
  const fadeIn  = 3;   // % del ciclo total dedicado al fade in
  const fadeOut = 3;   // % del ciclo total dedicado al fade out
  const keyframes = `@keyframes ${animName} {
      0%                        { opacity: 0; }
      ${fadeIn}%                { opacity: 1; }
      ${(pctVisible - fadeOut).toFixed(2)}% { opacity: 1; }
      ${pctVisible.toFixed(2)}% { opacity: 0; }
      100%                      { opacity: 0; }
    }`;

  return (
    <div id={contenedorId} style={wrapCarruselStyle(true)}>
      <div style={kickerStyle}>
        PUBLICIDAD <span style={{ opacity:0.5, marginLeft:8, fontSize:7 }}>{total} anunciantes</span>
      </div>
      <div style={{ ...itemsWrapStyle, position:'relative' }}>
        {pautas.map((p, i) => (
          <div key={p.id || i} style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            opacity: 0,
            animation: `${animName} ${totalSeg}s linear infinite`,
            animationDelay: `${i * CICLO_SEG}s`,
          }}>
            {renderPautaContenido(p)}
          </div>
        ))}
      </div>
      <style>{keyframes}</style>
    </div>
  );
}

// Contenido visual de una sola pauta (imagen enlace, o placeholder)
function renderPautaContenido(p) {
  if (p.tipoArchivo === 'imagen' && p.urlStorage) {
    return (
      <a href={p.urlDestino || '#'} target="_blank" rel="noopener noreferrer"
         style={{ display:'block', maxWidth:'100%', maxHeight:'100%' }}>
        <img src={p.urlStorage} alt={p.anunciante || 'Publicidad'}
          style={{ display:'block', maxWidth:'100%', maxHeight:'100%', objectFit:'contain' }} />
      </a>
    );
  }
  return (
    <div style={{ color:'#888', fontSize:11, fontStyle:'italic' }}>
      {p.anunciante || 'Espacio publicitario'}
    </div>
  );
}

const wrapCarruselStyle = (con) => ({
  background:'#faf7f0', border:'1px solid #e0d9c7', borderRadius:4,
  padding:'10px 16px 12px',
  display:'flex', flexDirection:'column', alignItems:'center',
  minHeight: 100,
});
const kickerStyle = {
  fontSize:8, letterSpacing:2, color:'#999', marginBottom:6, fontWeight:600,
};
const itemsWrapStyle = {
  flex:1, width:'100%', height: 90, minHeight: 90,
  display:'flex', alignItems:'center', justifyContent:'center',
};

function renderBack(edition, s) {
  return (
    <div className={s.backCover}>
      <div className={s.backCoverContent}>
        <div className={s.backCoverBrand}>Comercio Colaborativo</div>
        <div className={s.backCoverText}>
          Redeprog · Ecosistema Ecolab<br/>
          Edición Nº {edition.number}<br/>
          © 2026
        </div>
      </div>
    </div>
  );
}

function renderToc(page, edition, s) {
  return (
    <>
      <div className={s.tocSubtitle}>Edición Nº {edition.number}</div>
      <h1 className={s.tocTitle}>
        {page.partIndex === 0 ? 'Contenido' : 'Contenido (cont.)'}
      </h1>
      <hr className={s.tocDivider} />
      <ul className={s.tocList}>
        {page.items.map(item => (
          <li
            key={item.id}
            className={s.tocItem}
            data-goto={item._page ? item._page : undefined}
            style={{ cursor: item._page ? 'pointer' : 'default' }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className={s.tocItemTitle}>{item.title}</div>
              {item.category?.name && (
                <span className={s.tocCategory}>{item.category.name}</span>
              )}
            </div>
            <div className={s.tocDots} />
            <div className={s.tocPage}>{item._page || '·'}</div>
          </li>
        ))}
      </ul>
    </>
  );
}

function renderArticle(page, s) {
  const { article, chunkIndex, totalChunks, content } = page;
  const isFirst = chunkIndex === 0;
  const isLast = chunkIndex === totalChunks - 1;

  return (
    <>
      {isFirst && (
        <>
          {article.category?.name && (
            <div className={s.articleKicker}>{article.category.name}</div>
          )}
          <h2 className={s.articleTitle}>{article.title}</h2>
          <div className={s.articleMeta}>
            {article.author?.name || 'Redacción'}
            {article.publishedAt && ' · ' + new Date(article.publishedAt).toLocaleDateString('es-CO', {
              day: 'numeric', month: 'long', year: 'numeric',
            })}
          </div>
          {article.image && (
            <>
              <img
                src={article.image}
                alt={article.imageAlt || article.title}
                className={s.articleImage}
              />
              {article.imageAlt && (
                <div className={s.articleImageCaption}>{article.imageAlt}</div>
              )}
            </>
          )}
        </>
      )}
      {!isFirst && (
        <div className={s.articleKicker} style={{ opacity: 0.5 }}>
          {article.title} (cont.)
        </div>
      )}
      <div
        className={s.articleBody}
        dangerouslySetInnerHTML={{ __html: content }}
      />
      {isLast ? (
        <div className={s.articleEnd}>◆ ◆ ◆</div>
      ) : (
        <div className={s.continues}>continúa →</div>
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────
// Cálculo de tamaño según viewport
// ────────────────────────────────────────────────────────────────

function computeSize(formato = 'a4') {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Móvil (< 900px): siempre 1 página, ratio adaptado
  if (vw < 900) {
    const width = Math.min(vw - 40, 500);
    const ratios = { a4: 1.42, tabloide: 1.54, pantalla: 1.42 };
    const height = Math.min(vh - 180, width * (ratios[formato] || 1.42));
    return { width, height, singlePage: true };
  }

  const availableWidth  = vw - 80;
  const availableHeight = vh - 180;

  // Pantalla completa: una sola "página" panorámica que ocupa todo.
  // La página es horizontal (más ancha que alta) para permitir grid de
  // sección tipo home digital.
  if (formato === 'pantalla') {
    const pageWidth  = Math.min(availableWidth, 1600);
    const pageHeight = Math.min(availableHeight, Math.round(pageWidth * 0.62));
    return {
      width: Math.round(pageWidth),
      height: Math.round(pageHeight),
      singlePage: true,
    };
  }

  // A4 y Tabloide: spread de dos páginas.
  const ratio = formato === 'tabloide' ? 1.54 : 1.42; // A4 ≈ 1.414, Tabloide 28×43 ≈ 1.535
  let pageHeight = availableHeight;
  let pageWidth  = Math.floor(pageHeight / ratio);
  const bookWidth = pageWidth * 2;
  if (bookWidth > availableWidth) {
    pageWidth  = Math.floor(availableWidth / 2);
    pageHeight = Math.floor(pageWidth * ratio);
  }
  pageWidth  = Math.max(340, Math.min(780, pageWidth));
  pageHeight = Math.max(480, Math.min(1180, pageHeight));
  return { width: pageWidth, height: pageHeight, singlePage: false };
}
